import { formatQuantity } from '../../utils/inventoryLabels';

/**
 * The three movements of a supply. In Spanish here because the API labels them
 * in English, for Django's admin.
 */
export const SUPPLY_MOVEMENT_LABELS = {
  replenishment: 'Reposición',
  adjustment: 'Ajuste',
  consumption: 'Consumo',
  // A supply taken off a parcel goes back on a movement of its own.
  consumption_reversal: 'Devolución de consumo',
};

export const supplyMovementLabel = (value) => SUPPLY_MOVEMENT_LABELS[value] || value;

/** "$1.250", or a dash when there is no amount to show. */
export const formatMoney = (value) =>
  value === null || value === undefined || value === ''
    ? '-'
    : `$${Math.round(Number(value)).toLocaleString('es-CL')}`;

/** "35%", or a dash: without a known cost there is no margin, which is not zero. */
export const formatPercent = (value) =>
  value === null || value === undefined ? '-' : `${Math.round(Number(value) * 100)}%`;

export const formatSupplyMoment = (value) =>
  value
    ? new Date(value).toLocaleString('es-CL', { dateStyle: 'short', timeStyle: 'short' })
    : '-';

/* -------------------------------------------------------------------------- */
/*                                  The card                                  */
/* -------------------------------------------------------------------------- */

/**
 * What kind of thing a supply is. A fixed list: the containers (box, envelope,
 * bag) are the ones that lend the parcel their dimensions, and they are shown
 * first when packing. Two boxes of different sizes are two supplies, not two
 * kinds.
 */
export const SUPPLY_KIND_LABELS = {
  box: 'Caja',
  envelope: 'Sobre',
  bag: 'Bolsa',
  tape: 'Cinta',
  filler: 'Relleno',
  label: 'Etiqueta',
  other: 'Otro',
};

/** What wraps the parcel: the only kinds with dimensions and a tare. */
export const CONTAINER_KINDS = ['box', 'envelope', 'bag'];

export const isContainerKind = (kind) => CONTAINER_KINDS.includes(kind);

export const SUPPLY_KIND_OPTIONS = Object.entries(SUPPLY_KIND_LABELS).map(([value, label]) => ({
  value,
  label,
}));

export const supplyKindLabel = (value) => SUPPLY_KIND_LABELS[value] || value || '-';

export const isContainer = (supply) => isContainerKind(supply?.kind);

/** The four numbers of a container, in the order they are typed. */
export const BOX_FIELDS = [
  { key: 'length_cm', label: 'Largo', unit: 'cm' },
  { key: 'width_cm', label: 'Ancho', unit: 'cm' },
  { key: 'height_cm', label: 'Alto', unit: 'cm' },
  { key: 'tare_grams', label: 'Tara', unit: 'g' },
];

/** "20×15×10 cm · tara 80 g", or only what is known, or an empty string. */
export const boxLabel = (supply) => {
  if (!isContainer(supply)) return '';
  const parts = [];
  if (supply.length_cm && supply.width_cm && supply.height_cm) {
    parts.push(`${supply.length_cm}×${supply.width_cm}×${supply.height_cm} cm`);
  }
  if (supply.tare_grams) parts.push(`tara ${supply.tare_grams} g`);
  return parts.join(' · ');
};

export const EMPTY_SUPPLY_FORM = {
  sku: '',
  name: '',
  barcode: '',
  kind: 'other',
  length_cm: '',
  width_cm: '',
  height_cm: '',
  tare_grams: '',
  default_price: '',
  is_active: true,
};

/** @param {object|null} supply - As the backend answers it; `null` for a new one. */
export const supplyFormFrom = (supply) =>
  supply
    ? {
        sku: supply.sku || '',
        name: supply.name || '',
        barcode: supply.barcode || '',
        kind: supply.kind || 'other',
        length_cm: supply.length_cm ?? '',
        width_cm: supply.width_cm ?? '',
        height_cm: supply.height_cm ?? '',
        tare_grams: supply.tare_grams ?? '',
        default_price: supply.default_price ?? '',
        is_active: supply.is_active,
      }
    : { ...EMPTY_SUPPLY_FORM };

const wholeOrNull = (raw) => (raw === '' || raw === null || raw === undefined ? null : Math.round(Number(raw)));

/**
 * An emptied price travels as `null`: the supply is not charged, which is not
 * "charged zero". The container's numbers travel only for a container; for
 * anything else the server discards them anyway.
 */
export const buildSupplyPayload = (form) => {
  const container = isContainerKind(form.kind);
  return {
    sku: form.sku.trim(),
    name: form.name.trim(),
    barcode: form.barcode.trim(),
    kind: form.kind,
    length_cm: container ? wholeOrNull(form.length_cm) : null,
    width_cm: container ? wholeOrNull(form.width_cm) : null,
    height_cm: container ? wholeOrNull(form.height_cm) : null,
    tare_grams: container ? wholeOrNull(form.tare_grams) : null,
    default_price: form.default_price === '' ? null : Number(form.default_price),
    is_active: form.is_active,
  };
};

export const supplyProblem = (form) => {
  if (!form.sku.trim()) return 'Falta el SKU';
  if (!form.name.trim()) return 'Falta el nombre';
  if (isContainerKind(form.kind)) {
    const typed = BOX_FIELDS.filter(({ key }) => form[key] !== '');
    if (typed.some(({ key }) => !(Number(form[key]) > 0))) return 'Las medidas van en números mayores que cero';
    // Three dimensions or none: a container with two sides cannot fill a parcel.
    const sides = ['length_cm', 'width_cm', 'height_cm'].filter((key) => form[key] !== '').length;
    if (sides > 0 && sides < 3) return `Faltan medidas: van las tres o ninguna`;
  }
  return null;
};

/* -------------------------------------------------------------------------- */
/*                               Replenishment                                */
/* -------------------------------------------------------------------------- */

export const EMPTY_REPLENISH_FORM = { quantity: '1', unit_cost: '', reference: '' };

/**
 * The average cost a purchase would leave, worked out before saving.
 *
 * Moving weighted average: what there was at its cost, plus what comes in at
 * the cost of this purchase, over the new balance. A small purchase over a big
 * balance barely moves it, and showing it first avoids the surprise. With an
 * empty balance there is nothing old to weigh: it is the cost of this purchase.
 *
 * @returns {number|null} `null` while the form does not allow working it out.
 */
export const projectedAverageCost = (supply, form) => {
  const quantity = Number(form.quantity);
  const cost = Number(form.unit_cost);
  if (!supply || !(quantity > 0) || form.unit_cost === '' || Number.isNaN(cost)) return null;

  const previousQuantity = Number(supply.quantity);
  if (previousQuantity <= 0) return cost;

  const previousCost = Number(supply.average_cost);
  return (previousQuantity * previousCost + quantity * cost) / (previousQuantity + quantity);
};

export const replenishProblem = (form) => {
  if (!(Number(form.quantity) > 0)) return 'Indica cuánto ingresa';
  if (form.unit_cost === '') return 'Falta el costo unitario';
  if (Number(form.unit_cost) < 0) return 'El costo no puede ser negativo';
  return null;
};

export const buildReplenishPayload = (form) => ({
  quantity: form.quantity,
  unit_cost: form.unit_cost,
  reference: form.reference.trim(),
});

/* -------------------------------------------------------------------------- */
/*                                 Adjustment                                 */
/* -------------------------------------------------------------------------- */

export const EMPTY_ADJUST_FORM = { quantity: '', notes: '' };

/**
 * What is still missing before the adjustment can be sent.
 *
 * The reason is mandatory: an adjustment with no explanation is a difference
 * nobody will be able to reconstruct later.
 */
export const adjustProblem = (supply, form) => {
  const delta = Number(form.quantity);
  if (form.quantity === '' || Number.isNaN(delta) || delta === 0) return 'Indica el ajuste';

  const balance = Number(supply?.quantity ?? 0);
  if (balance + delta < 0) return `Solo hay ${formatQuantity(balance)}`;
  if (!form.notes.trim()) return 'Falta el motivo';
  return null;
};

export const buildAdjustPayload = (form) => ({
  quantity: form.quantity,
  notes: form.notes.trim(),
});

/* -------------------------------------------------------------------------- */
/*                              Prices by client                              */
/* -------------------------------------------------------------------------- */

/** `{ [companyId]: price }` with the exceptions the supply has today. */
export const pricesFormFrom = (supply) =>
  Object.fromEntries((supply?.client_prices || []).map((entry) => [entry.company, entry.price]));

/**
 * One entry per client of the warehouse. An emptied field travels as `null`,
 * which removes the exception and sends that client back to the default price.
 *
 * @param {object} form - `{ [companyId]: string|number }`
 * @param {Array<{value: number}>} clients - Clients with an active contract there.
 */
export const buildPricesPayload = (form, clients) =>
  Object.fromEntries(
    clients.map((client) => {
      const raw = form[client.value];
      return [client.value, raw === '' || raw === undefined || raw === null ? null : Number(raw)];
    })
  );

/* -------------------------------------------------------------------------- */
/*                                    Excel                                   */
/* -------------------------------------------------------------------------- */

export const buildSuppliesExportRows = (supplies) =>
  supplies.map((supply) => ({
    Bodega: supply.warehouse_name,
    SKU: supply.sku,
    EAN: supply.barcode || '',
    Nombre: supply.name,
    Tipo: supplyKindLabel(supply.kind),
    Medidas: boxLabel(supply),
    'Costo promedio': Number(supply.average_cost),
    Precio: supply.default_price ?? '',
    Saldo: Number(supply.quantity),
    'Valor stock': Number(supply.stock_value),
    Estado: supply.is_active ? 'Activo' : 'De baja',
  }));

/** @param {object|null} warehouse - The warehouse in use, or `null` for all. */
export const suppliesExportFileName = (warehouse) =>
  `insumos-${warehouse ? warehouse.name : 'todas'}.xlsx`;
